import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { Info } from 'lucide-react';
import { createUsageAnalyticsService } from '../../../services/usageAnalyticsService';
import platformAdminService from '../../../services/platformAdminService';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import UsageFiltersBar from './UsageFiltersBar';
import UsageSummaryCards from './UsageSummaryCards';
import UsageTimeseriesChart from './UsageTimeseriesChart';
import UsageBreakdownTable from './UsageBreakdownTable';
import UsageRunsDialog from './UsageRunsDialog';
import {
  getBrowserTz,
  toExclusiveEndDate,
  computeBucketRange,
  formatBucket,
  dimensionCellValue,
  DIMENSION_DRILLDOWN,
} from './usageFormat';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './UsageAnalyticsPanel.module.scss';

const PAGE_SIZE = 50;

const platformCompanyOptions = async () => {
  const data = await platformAdminService.listCompanies(false, { limit: 200 });
  return data?.companies || (Array.isArray(data) ? data : []);
};

// Per-surface defaults (Partner tier guide §5.7 table). Any of these can be
// overridden by props — e.g. a partner's detail page on Platform Admin uses the
// platform endpoints scoped to one partner.
const PRESETS = {
  company: {
    defaultGroupBy: ['user', 'model'],
    hiddenDimensions: ['company', 'partner'],
    topEntitiesTitle: 'Top users',
    loadCompanyOptions: null,
  },
  platform: {
    defaultGroupBy: ['company', 'model'],
    hiddenDimensions: [],
    topEntitiesTitle: 'Top companies',
    loadCompanyOptions: platformCompanyOptions,
  },
  partner: {
    defaultGroupBy: ['company', 'model'],
    hiddenDimensions: ['partner'],
    topEntitiesTitle: 'Top companies',
    loadCompanyOptions: null,
  },
};

/**
 * LLM Usage & Token Consumption Analytics — one screen parameterised by
 * basePath + scope ('company' | 'platform' | 'partner'), mounted in Company
 * Admin, Platform Admin (incl. a partner's detail page) and the Partner area
 * (identical query params / response shapes on every surface per spec).
 *
 * Optional overrides (pass stable identities — memoize objects/functions):
 *   defaultGroupBy, hiddenDimensions, loadCompanyOptions() → [{id,name}],
 *   companyLabelFn(id, name), partners (Partner filter), fixedParams (merged
 *   into every request, e.g. { partner_id }), redactedMessage, emptyMessage,
 *   dateTooltip.
 */
const UsageAnalyticsPanel = ({
  basePath,
  scope,
  notify,
  defaultGroupBy,
  hiddenDimensions,
  loadCompanyOptions,
  companyLabelFn = null,
  partners = null,
  fixedParams = null,
  redactedMessage = null,
  emptyMessage = null,
  dateTooltip = null,
}) => {
  const preset = PRESETS[scope] || PRESETS.company;
  const groupByDefault = defaultGroupBy || preset.defaultGroupBy;
  const groupByDefaultKey = groupByDefault.join(',');
  const dimsHidden = hiddenDimensions || preset.hiddenDimensions;
  const companyLoader = loadCompanyOptions === undefined ? preset.loadCompanyOptions : loadCompanyOptions;

  const service = useMemo(() => createUsageAnalyticsService(basePath), [basePath]);
  const tz = useMemo(() => getBrowserTz(), []);

  const [dimensions, setDimensions] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [partnerId, setPartnerId] = useState('');

  const [windowValue, setWindowValue] = useState('30d');
  const [dateRange, setDateRange] = useState({ start: '', end: '' }); // inclusive UI dates; empty ⇒ use windowValue
  const [runType, setRunType] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [groupBy, setGroupBy] = useState(() => groupByDefault);
  const [orderBy, setOrderBy] = useState('total_tokens');
  const [orderDir, setOrderDir] = useState('desc');
  const [splitBy, setSplitBy] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [offset, setOffset] = useState(0);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [timeseries, setTimeseries] = useState(null);
  const [timeseriesLoading, setTimeseriesLoading] = useState(false);
  const [error, setError] = useState(null);

  const [runsDialog, setRunsDialog] = useState(null); // { title, filters }

  // Reset grouping when the surface's default changes (e.g. Platform switches to
  // partner,model once it learns partners exist).
  useEffect(() => {
    setGroupBy(groupByDefaultKey.split(','));
  }, [groupByDefaultKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await service.getDimensions();
        if (!cancelled) setDimensions(data);
      } catch (err) {
        // Non-fatal — filter selects fall back to the spec's static lists.
        console.warn('Failed to load usage dimensions', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [service]);

  useEffect(() => {
    if (!companyLoader) {
      setCompanies([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await companyLoader();
        if (!cancelled) setCompanies(Array.isArray(list) ? list : []);
      } catch (err) {
        console.warn('Failed to load companies for usage drill-down', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyLoader]);

  const baseParams = useMemo(() => {
    const p = { tz, ...(fixedParams || {}) };
    if (dateRange.start && dateRange.end) {
      p.start = dateRange.start;
      p.end = toExclusiveEndDate(dateRange.end);
    } else {
      p.window = windowValue;
    }
    if (runType && runType !== 'all') p.run_type = runType;
    if (status && status !== 'all') p.status = status;
    if (debouncedSearch) p.search = debouncedSearch;
    if (companyLoader && companyId) p.company_id = companyId;
    if (partners && partnerId) p.partner_id = partnerId;
    return p;
  }, [tz, fixedParams, dateRange, windowValue, runType, status, debouncedSearch, companyLoader, companyId, partners, partnerId]);

  // Any filter change (other than pure pagination) goes back to page 1.
  useEffect(() => {
    setOffset(0);
  }, [baseParams, groupBy, orderBy, orderDir]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setSummaryLoading(true);
        const data = await service.getSummary(baseParams);
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled) setError(err?.data?.detail || err?.message || 'Failed to load usage summary.');
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [service, baseParams, refreshNonce]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setTimeseriesLoading(true);
        const params = { ...baseParams };
        if (splitBy) params.split_by = splitBy;
        const data = await service.getTimeseries(params);
        if (!cancelled) setTimeseries(data);
      } catch (err) {
        if (!cancelled) setError(err?.data?.detail || err?.message || 'Failed to load usage trend.');
      } finally {
        if (!cancelled) setTimeseriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [service, baseParams, splitBy, refreshNonce]);

  const loadBreakdown = useCallback(async () => {
    try {
      setBreakdownLoading(true);
      setError(null);
      const data = await service.getBreakdown({
        ...baseParams,
        group_by: groupBy.join(','),
        order_by: orderBy,
        order_dir: orderDir,
        limit: PAGE_SIZE,
        offset,
      });
      setBreakdown(data);
    } catch (err) {
      setError(err?.data?.detail || err?.message || 'Failed to load usage breakdown.');
    } finally {
      setBreakdownLoading(false);
    }
  }, [service, baseParams, groupBy, orderBy, orderDir, offset]);

  useEffect(() => {
    loadBreakdown();
  }, [loadBreakdown, refreshNonce]);

  const refreshAll = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const openRunsFromRow = useCallback(
    (row) => {
      if (!breakdown) return;
      const grain = breakdown?.range?.granularity || 'day';
      const filters = { tz };
      if (baseParams.window) filters.window = baseParams.window;
      if (baseParams.start) filters.start = baseParams.start;
      if (baseParams.end) filters.end = baseParams.end;
      if (baseParams.run_type) filters.run_type = baseParams.run_type;
      if (baseParams.status) filters.status = baseParams.status;
      if (baseParams.company_id) filters.company_id = baseParams.company_id;
      if (baseParams.partner_id) filters.partner_id = baseParams.partner_id;

      const labelParts = [];
      (breakdown.group_by || []).forEach((dim) => {
        if (dim === 'date') {
          const { start, end } = computeBucketRange(row.bucket, grain);
          if (start) {
            filters.start = start;
            filters.end = end;
            delete filters.window;
          }
          labelParts.push(formatBucket(row.bucket, grain));
        } else {
          const dd = DIMENSION_DRILLDOWN[dim];
          if (dd && row[dd.rowField] != null) filters[dd.param] = row[dd.rowField];
          labelParts.push(dimensionCellValue(dim, row, grain, companyLabelFn));
        }
      });

      setRunsDialog({ title: labelParts.filter(Boolean).join(' · ') || 'Runs', filters });
    },
    [breakdown, baseParams, tz, companyLabelFn]
  );

  const handleExport = useCallback(
    async (mode, format) => {
      try {
        const exportParams = { ...baseParams };
        if (mode === 'breakdown') exportParams.group_by = groupBy.join(',');
        const { truncated } = await service.exportData({ mode, format, ...exportParams });
        if (truncated) {
          notify(
            'Export capped at the row limit — some rows were left out. The file notes the truncation.',
            'warning'
          );
        } else {
          notify('Export downloaded.', 'success');
        }
      } catch (err) {
        notify(err?.data?.detail || err?.message || 'Export failed.', 'error');
      }
    },
    [service, baseParams, groupBy, notify]
  );

  return (
    <Box className={styles.wrap}>
      <Box className={sharedStyles.noteBanner}>
        <Info size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Batch/loop jobs count as one aggregate row per job. Background runs are recorded once
          their status is polled — a result that's never fetched is never recorded. Utility-model
          calls (query rewriting, memory extraction, etc.) aren't attributed to a run, so these
          totals are real but not a complete provider bill.
        </span>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <UsageFiltersBar
        hiddenDimensions={dimsHidden}
        showCompanyFilter={!!companyLoader}
        companyLabelFn={companyLabelFn}
        partners={partners}
        partnerId={partnerId}
        onPartnerChange={setPartnerId}
        dateTooltip={dateTooltip}
        dimensions={dimensions}
        windowValue={windowValue}
        onWindowChange={(v) => {
          setWindowValue(v);
          setDateRange({ start: '', end: '' });
        }}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        runType={runType}
        onRunTypeChange={setRunType}
        status={status}
        onStatusChange={setStatus}
        search={search}
        onSearchChange={setSearch}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        orderBy={orderBy}
        onOrderByChange={setOrderBy}
        orderDir={orderDir}
        onOrderDirChange={setOrderDir}
        splitBy={splitBy}
        onSplitByChange={setSplitBy}
        companies={companies}
        companyId={companyId}
        onCompanyChange={setCompanyId}
        loading={summaryLoading || breakdownLoading || timeseriesLoading}
        onRefresh={refreshAll}
        onExport={handleExport}
      />

      <UsageSummaryCards
        summary={summary}
        loading={summaryLoading && !summary}
        // Partner leaderboard is companies, or users once drilled into one (§5.7).
        topEntitiesTitle={scope === 'partner' && companyId ? 'Top users' : preset.topEntitiesTitle}
        companyLabelFn={companyLabelFn}
      />

      <Box className={sharedStyles.tableCard} sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Usage over time
        </Typography>
        {timeseriesLoading && !timeseries ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <UsageTimeseriesChart data={timeseries} />
        )}
      </Box>

      <UsageBreakdownTable
        breakdown={breakdown}
        loading={breakdownLoading}
        offset={offset}
        limit={PAGE_SIZE}
        onOffsetChange={setOffset}
        onRowClick={openRunsFromRow}
        companyLabelFn={companyLabelFn}
        emptyMessage={emptyMessage}
      />

      {runsDialog && (
        <UsageRunsDialog
          open={!!runsDialog}
          title={runsDialog.title}
          filters={runsDialog.filters}
          service={service}
          redactedMessage={redactedMessage}
          onClose={() => setRunsDialog(null)}
        />
      )}
    </Box>
  );
};

export default UsageAnalyticsPanel;
